import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const RELOAD_FLAG = 'routeError:chunkReloadAt';

const CHUNK_LOAD_MESSAGE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk [\w-]+ failed/i;

// ZLAYT-1485: after a release, a tab still running the previous build can get
// a chunk with no default export; React.lazy then reads `.default` of
// undefined. Chrome/Firefox and Safari word it differently.
const MISSING_DEFAULT_MESSAGE =
  /Cannot read properties of undefined \(reading 'default'\)|undefined is not an object \(evaluating '[^']*\.?default'\)/i;

/** True for the error a browser raises when a lazy route chunk is gone. */
export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error && error.name === 'ChunkLoadError') return true;
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    CHUNK_LOAD_MESSAGE.test(message) || MISSING_DEFAULT_MESSAGE.test(message)
  );
}

/**
 * Reload the page for a stale release, at most once a minute per tab, so a
 * chunk that is really gone cannot cause a reload loop. Shared by this page
 * and lazyWithRetry. Returns true when a reload was started.
 */
export function reloadOnceForStaleRelease(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (Date.now() - last > 60_000) {
      sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
      window.location.reload();
      return true;
    }
  } catch {
    // storage unavailable: fall through to the manual reload button
  }
  return false;
}

/**
 * Route-level error element (ZLAYT-1406, QA D-10; see ZLAYT-1389).
 *
 * React Router's data router catches render errors in routes itself, so the
 * app-level ErrorBoundary never sees them and the user got React Router's
 * bare "Unexpected Application Error!" page. This reports the error and
 * offers a reload. After a deploy, an open tab can request a lazy chunk that
 * no longer exists; that case reloads once automatically.
 */
function RouteErrorPage() {
  const error = useRouteError();

  useEffect(() => {
    console.error('[RouteErrorPage] route render error:', error);
    Sentry.captureException(error);
    if (isChunkLoadError(error)) reloadOnceForStaleRelease();
  }, [error]);

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        textAlign: 'center',
        color: '#1a1a2e',
      }}
    >
      <h1 style={{ fontSize: '24px', marginBottom: '12px' }}>
        Something went wrong
      </h1>
      <p style={{ fontSize: '14px', color: '#556', marginBottom: '24px' }}>
        This page hit an unexpected error and could not finish loading. Our team
        has been notified. Reloading the page usually recovers.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: 600,
          color: '#fff',
          background: '#2b4a7a',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
        }}
      >
        Reload page
      </button>
    </div>
  );
}

export default RouteErrorPage;
